/*
 * Copyright (c) 2025, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import { observer } from "mobx-react-lite"
import type { JSX } from "react"
import { useState } from "react"

import { ApplicationTypeViewModel } from "../../features/application-type/application-type-viewmodel"
import { UserAppTypeViewModel } from "../../features/user/user-apptype-viewmodel"
import { useViewModel } from "../app/app-hooks"
import { ApplicationTypeRowView } from "../application-type/application-type-row-view"

interface UserEditAppsModalProps {
    userId: number
    orgId: number
    onClose: () => void
    onSave: (selectedAppTypeIds: number[]) => void
}

export const UserEditAppsModal = observer(({ userId, orgId, onClose, onSave }: UserEditAppsModalProps): JSX.Element => {
    const userAppTypes = useViewModel(UserAppTypeViewModel).userAppTypesForOrganization(userId, orgId)
    const orgAppTypes = useViewModel(ApplicationTypeViewModel).allAppTypes

    const [selectedAppTypeIds, setSelectedAppTypeIds] = useState(() =>
        userAppTypes.map((app) => app.id).filter((id): id is number => id !== undefined)
    )

    const toggleAppTypeSelection = (id: number) => {
        setSelectedAppTypeIds((appType) =>
            appType.includes(id) ? appType.filter((existingId) => existingId !== id) : [...appType, id]
        )
    }

    return (
        <div className="modal block modal-on-background" tabIndex={-1} role="dialog" onClick={onClose}>
            <div
                className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable"
                role="document"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="modal-content">
                    <div className="modal-header custom-card-header bg-legacy-primary text-white">
                        <h5 className="modal-title">Edit Assigned Applications</h5>
                        <button
                            type="button"
                            className="btn-close btn-close-white"
                            aria-label="Close"
                            onClick={onClose}
                        />
                    </div>
                    <div className="modal-body py-legacy">
                        <div className="text-legacy-muted mb-legacy">Select application types assigned to user.</div>

                        {orgAppTypes.length > 0 && (
                            <div className="flex gap-2 mb-2">
                                <button
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={() => {
                                        // get app ids
                                        const appTypeIds = orgAppTypes
                                            .map((appType) => appType.id)
                                            .filter((id): id is number => typeof id === "number")
                                        setSelectedAppTypeIds(appTypeIds)
                                    }}
                                >
                                    Select All
                                </button>
                                <button
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={() => {
                                        setSelectedAppTypeIds([])
                                    }}
                                >
                                    Deselect All
                                </button>
                            </div>
                        )}

                        <div className="table-responsive rounded-legacy-md">
                            <table className="table table-sm m-0">
                                <tbody>
                                    {orgAppTypes.length > 0 ? (
                                        orgAppTypes
                                            .filter((appType) => typeof appType.id === "number")
                                            .map((appType) => {
                                                const selected = selectedAppTypeIds.includes(appType.id ?? -1)
                                                return (
                                                    <ApplicationTypeRowView
                                                        onClick={() =>
                                                            appType.id !== undefined &&
                                                            toggleAppTypeSelection(appType.id)
                                                        }
                                                        appType={appType}
                                                        selected={selected}
                                                        selectable={true}
                                                        key={appType.id ?? 0}
                                                    />
                                                )
                                            })
                                    ) : (
                                        <tr>
                                            <td colSpan={4} className="text-center text-legacy-muted py-legacy">
                                                No application types available for this organization.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                    <div className="modal-footer">
                        <button className="btn btn-secondary" onClick={onClose}>
                            Cancel
                        </button>
                        <button className="btn w-button" onClick={() => onSave(selectedAppTypeIds)}>
                            Assign Applications
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
})
